import { createApi } from '@reduxjs/toolkit/query/react';

import { StaffNoteCreatedEvent } from '../types/events/staff-note-events';
import { DomainEventSchema, FinchEventCode } from '../types/events-types';
import {
  CreateStaffNote,
  StaffNote,
  UpdateStaffNote,
} from '../types/staff-notes-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import {
  EventOrderingField,
  eventsApi,
  EventsFilterParams,
  PaginatedEventsResponse,
} from './eventsService';
import { userApiClient } from './userService';

/**
 * Generates consistent optimistic IDs for staff note events
 */
const generateOptimisticIds = () => {
  const timestamp = Date.now();
  return {
    eventId: `temp-${timestamp}`,
    noteId: `temp-note-${timestamp}`,
  };
};

export const staffNotesApi = createApi({
  reducerPath: 'staffNotesApi',
  tagTypes: ['StaffNotes', 'StaffNote'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // List staff notes for a matter
    getStaffNotes: builder.query<StaffNote[], { matterId: string }>({
      query: ({ matterId }) => `api/matters/${matterId}/staff-notes/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'StaffNotes', id: matterId },
        ...(result || []).map(({ id }) => ({ type: 'StaffNote' as const, id })),
      ],
    }),

    // Get a specific staff note
    getStaffNote: builder.query<
      StaffNote,
      { matterId: string; noteId: string }
    >({
      query: ({ matterId, noteId }) =>
        `api/matters/${matterId}/staff-notes/${noteId}/`,
      providesTags: (result, error, { noteId }) => [
        { type: 'StaffNote', id: noteId },
      ],
    }),

    // Create a new staff note with optimistic update
    createStaffNote: builder.mutation<
      StaffNote,
      { matterId: string; note: Omit<CreateStaffNote, 'matter_id'> }
    >({
      query: ({ matterId, note }) => ({
        url: `api/matters/${matterId}/staff-notes/`,
        method: 'POST',
        body: { ...note, matter_id: matterId },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'StaffNotes', id: matterId },
      ],
      // Add optimistic update to immediately show the note in the activity feed
      async onQueryStarted(
        { matterId, note },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get current user from the store
        const currentUserResult =
          userApiClient.endpoints.getCurrentUser.select()(getState() as any);
        const currentUser = currentUserResult?.data;

        // Generate IDs once to ensure consistency
        const { eventId, noteId } = generateOptimisticIds();

        // Create an optimistic domain event for the new staff note
        const optimisticEvent: DomainEventSchema<FinchEventCode.STAFF_NOTE_CREATED> =
          {
            id: eventId,
            occurred_at: new Date().toISOString(),
            matter_id: matterId,
            event_code: FinchEventCode.STAFF_NOTE_CREATED,
            payload: {
              target_id: noteId,
              content: note.content,
              matter_id: matterId,
            } satisfies StaffNoteCreatedEvent,
            target_type: 'app.staff_note',
            target_id: noteId,
            user: currentUser || null,
            agent: null,
            correlation_id: null,
          };

        // Properly typed query parameters
        const queryParams: { matterId: string } & EventsFilterParams = {
          matterId,
          event_code_not_in: [
            FinchEventCode.BLOB_TRANSFORM_COMPLETED,
            FinchEventCode.DELIVERABLE_CREATED,
            FinchEventCode.DELIVERABLE_STATUS_CHANGED,
            FinchEventCode.DELIVERABLE_SATISFIED,
            FinchEventCode.DELIVERABLE_MATCH_STATUS_CHANGED,
            FinchEventCode.DELIVERABLE_MATCH_APPROVED,
            FinchEventCode.ARTIFACT_CREATED,
            FinchEventCode.ARTIFACT_DIGEST_CREATED,
            FinchEventCode.ARTIFACT_DIGEST_STATUS_UPDATED,
          ],
          limit: 50,
          ordering: ['-occurred_at' as EventOrderingField],
        };

        // Optimistically update the events cache
        const patchResult = dispatch(
          eventsApi.util.updateQueryData(
            'getMatterEvents',
            queryParams,
            (
              draft: PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
            ) => {
              if (draft && draft.results) {
                // Add the optimistic event at the beginning of the results
                draft.results.unshift(optimisticEvent);
              }
            },
          ),
        );

        try {
          // Wait for the mutation to complete
          await queryFulfilled;

          // After successful creation, invalidate events to fetch the real event
          dispatch(
            eventsApi.util.invalidateTags([{ type: 'Events', id: matterId }]),
          );
        } catch {
          // If the mutation fails, revert the optimistic update
          patchResult.undo();
        }
      },
    }),

    // Update a staff note
    updateStaffNote: builder.mutation<
      StaffNote,
      {
        matterId: string;
        noteId: string;
        note: Omit<UpdateStaffNote, 'id' | 'matter_id'>;
      }
    >({
      query: ({ matterId, noteId, note }) => ({
        url: `api/matters/${matterId}/staff-notes/${noteId}/`,
        method: 'PUT',
        body: { ...note, id: noteId, matter_id: matterId },
      }),
      invalidatesTags: (result, error, { matterId, noteId }) => [
        { type: 'StaffNotes', id: matterId },
        { type: 'StaffNote', id: noteId },
      ],
    }),

    // Partially update a staff note
    partialUpdateStaffNote: builder.mutation<
      StaffNote,
      {
        matterId: string;
        noteId: string;
        note: Partial<Omit<UpdateStaffNote, 'id' | 'matter_id'>>;
      }
    >({
      query: ({ matterId, noteId, note }) => ({
        url: `api/matters/${matterId}/staff-notes/${noteId}/`,
        method: 'PATCH',
        body: note,
      }),
      invalidatesTags: (result, error, { matterId, noteId }) => [
        { type: 'StaffNotes', id: matterId },
        { type: 'StaffNote', id: noteId },
      ],
    }),

    // Toggle pin status on a staff note
    togglePinStaffNote: builder.mutation<
      StaffNote,
      { matterId: string; noteId: string; is_pinned: boolean }
    >({
      query: ({ matterId, noteId, is_pinned }) => ({
        url: `api/matters/${matterId}/staff-notes/${noteId}/`,
        method: 'PATCH',
        body: { is_pinned },
      }),
      invalidatesTags: (result, error, { matterId, noteId }) => [
        { type: 'StaffNotes', id: matterId },
        { type: 'StaffNote', id: noteId },
      ],
      async onQueryStarted(
        { matterId, noteId, is_pinned },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          staffNotesApi.util.updateQueryData(
            'getStaffNotes',
            { matterId },
            (draft) => {
              const note = draft.find((n) => n.id === noteId);
              if (note) {
                note.is_pinned = is_pinned;
                note.pinned_at = is_pinned ? new Date().toISOString() : null;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Delete a staff note
    deleteStaffNote: builder.mutation<
      void,
      { matterId: string; noteId: string }
    >({
      query: ({ matterId, noteId }) => ({
        url: `api/matters/${matterId}/staff-notes/${noteId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, noteId }) => [
        { type: 'StaffNotes', id: matterId },
        { type: 'StaffNote', id: noteId },
      ],
    }),
  }),
});

export const {
  useGetStaffNotesQuery,
  useGetStaffNoteQuery,
  useCreateStaffNoteMutation,
  useUpdateStaffNoteMutation,
  usePartialUpdateStaffNoteMutation,
  useDeleteStaffNoteMutation,
  useTogglePinStaffNoteMutation,
} = staffNotesApi;
